/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, RefObject } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { CalendarDate, CalendarDateRequired } from '#utils/UNSAFE_calendarDateUtils';
import { calendarDatesAreDifferent } from '#utils/PRIVATE_dayUtils';
import { DatePicker } from '#UNSAFE_DatePicker';
import { CloseDetail, InputDatePickerDropdown } from './InputDatePickerDropdown';
import { IconButton } from '#UNSAFE_IconButton';
import { InputDateMask } from '#UNSAFE_InputDateMask';
import { InputDatePicker } from './InputDatePicker';
import { useInputDatePickerState } from './useInputDatePickerState';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';

type InputDatePickerProps = ComponentProps<typeof InputDatePicker>;
type InputDateMaskProps = ComponentProps<typeof InputDateMask>;
type DatePickerProps = ComponentProps<typeof DatePicker>;
type IconButtonProps = ComponentProps<typeof IconButton>;
type DropdownProps = ComponentProps<typeof InputDatePickerDropdown>;

type Props = {
  onCommit: InputDatePickerProps['onCommit'];
  onInput: InputDatePickerProps['onInput'];
  value: InputDatePickerProps['value'];
};

/**
 * Hook that provides handlers and other functionality for InputDatePicker.
 */
const useInputDatePicker = ({ onCommit, onInput, value: currentValue }: Props) => {
  // Whether the dropdown is open.
  const [isOpen, setIsOpen] = useState(false);

  // Ref to the calendar icon button.
  const iconRef: RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null);

  // Ref to the InputDateMask component.
  const maskRef: RefObject<HTMLElement> = useRef<HTMLElement>(null);

  // Keep track of whether the dropdown was just dismissed from an outside mousedown event.
  const outsideMousedownRef = useRef<boolean>(false);
  const outsideMousedownTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  // Keep track of whether the calendar icon is focused after a date is selected
  // and the date picker is closed.
  const calendarFocusAfterSelect = useRef<boolean>(false);
  const calendarFocusAfterSelectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  // Keep track of the last date values passed to onInput and onCommit.
  const lastCommittedDateRef = useRef<CalendarDate | undefined>(currentValue);
  const lastDateRef = useRef<CalendarDate | undefined>(currentValue);

  // Keep track of the last controlled value passed from the parent, so we can detect a programmatic change.
  const lastValueFromParentRef = useRef<CalendarDate | undefined>(currentValue);

  // Keep track of whether a date was selected in the date picker.
  const dateSelectedRef = useRef<boolean>(false);

  const { state, dispatch } = useInputDatePickerState({ value: currentValue });

  // If a new value is being pushed from the parent, and that value differs from
  // the current value of the reducer, then this is a programmatic update
  // and we need to call 'reset' to update the reducer state. Otherwise, the only
  // time the reducer state is updated from the value is on mount.
  if (calendarDatesAreDifferent(lastValueFromParentRef.current, currentValue)) {
    lastValueFromParentRef.current = currentValue;
    if (calendarDatesAreDifferent(currentValue, state.dateValue)) {
      dispatch({ type: 'reset', data: currentValue });
      lastDateRef.current = currentValue;
      lastCommittedDateRef.current = currentValue;
    }
  }

  // Reducer updates are async, so wait until the state updates and then call our callbacks.
  useEffect(() => {
    if (calendarDatesAreDifferent(state.dateValue, lastDateRef.current)) {
      onInput({ previousValue: lastDateRef.current, value: state.dateValue });
      lastDateRef.current = state.dateValue;
    }
    if (
      dateSelectedRef.current &&
      calendarDatesAreDifferent(state.dateValue, lastCommittedDateRef.current)
    ) {
      onCommit?.({ previousValue: lastCommittedDateRef.current, value: state.dateValue });
      lastCommittedDateRef.current = state.dateValue;
      dateSelectedRef.current = false;
    }
  }, [onCommit, onInput, state.dateValue]);

  // Handle onInput from InputDateMask as segment values are being added or changed in the input field.
  // We wait for the state to update before calling onInput.
  const maskInputHandler = useCallback<InputDateMaskProps['onInput']>(
    (detail: ValueUpdateDetail<CalendarDate>) => {
      dispatch({ type: 'dateChanged', data: detail.value });
    },
    [dispatch]
  );

  // Handle onCommit from InputDateMask, which is called when the user presses Enter or the field is blurred.
  const maskCommitHandler = useCallback<NonNullable<InputDateMaskProps['onCommit']>>(() => {
    // If the current date value differs from the last committed value, then call onCommit.
    if (calendarDatesAreDifferent(lastCommittedDateRef.current, state.dateValue)) {
      onCommit?.({ value: state.dateValue, previousValue: lastCommittedDateRef.current });
      lastCommittedDateRef.current = state.dateValue;
    }
  }, [onCommit, state.dateValue]);

  // Respond to onAction event from the calendar icon button. Toggles the date picker dropdown.
  const calendarIconActionHandler = useCallback<NonNullable<IconButtonProps['onAction']>>(() => {
    // Only toggle the value of isOpen if we didn't just dismiss the dropdown.
    if (!outsideMousedownRef.current && !calendarFocusAfterSelect.current) {
      setIsOpen((prevIsOpen) => !prevIsOpen);
    }
    outsideMousedownRef.current = false;
    calendarFocusAfterSelect.current = false;
  }, []);

  const dropdownCloseHandler = useCallback<NonNullable<DropdownProps['onClose']>>(
    (detail: CloseDetail) => {
      // Keep track of when the dropdown is closed via outside mousedown, including when the calendar icon
      // is clicked. We need this in order to toggle visibility of the picker and dropdown.
      if (detail.reason === 'outsideMousedown') {
        outsideMousedownRef.current = true;
        clearTimeout(outsideMousedownTimerRef.current);

        // Clear the flag after a short delay to ensure the dropdown can be opened again.
        outsideMousedownTimerRef.current = setTimeout(() => {
          outsideMousedownRef.current = false;
        }, 200);
      } else if (detail.reason === 'dismissed' || detail.reason === 'keyboardDismissed') {
        // Focus the calendar icon when the dropdown is closed.
        iconRef.current?.focus();
      }
      setIsOpen(false);
    },
    []
  );

  // Handle date selection in the picker.
  //   - Updates the value in state.
  //   - Closes the dropdown.
  //   - Sets focus to the date field.
  const datePickerCommitHandler = useCallback<DatePickerProps['onCommit']>(
    (detail: ValueUpdateDetail<CalendarDateRequired>) => {
      // Set focus to the calendar icon when a date is selected.
      iconRef.current?.focus();

      // If user is using the keyboard, date selection happens on keyDown.
      // If we transfer focus to the calendar icon, it then sees the corresponding
      // keyUp and thinks the button was triggered again (usePress reacts to
      // keyUp, not keyDown). Maintain a flag that prevents this from happening,
      // and clear it after a short delay in case selection was made with the mouse.
      // TODO: Investigate a better way to prevent the dropdown from reopening (JET-67580).
      calendarFocusAfterSelect.current = true;
      clearTimeout(calendarFocusAfterSelectTimerRef.current);
      calendarFocusAfterSelectTimerRef.current = setTimeout(() => {
        calendarFocusAfterSelect.current = false;
      }, 500);

      // Set a ref indicating a date was selected.
      dateSelectedRef.current = true;
      dispatch({ type: 'dateSelected', data: detail.value });
      setIsOpen(false);
    },
    [dispatch]
  );

  return {
    calendarIconActionHandler,
    datePickerCommitHandler,
    dropdownCloseHandler,
    completeDateValue: state.completeDateValue,
    dateValue: state.dateValue,
    iconRef,
    isOpen,
    maskCommitHandler,
    maskInputHandler,
    maskRef
  };
};

export { useInputDatePicker };
